import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePatientChartSession } from 'infrastructure/hooks/patient/use-patient-chart-session';
import { usePatientAdminPanelSession } from 'infrastructure/hooks/patient/use-patient-admin-panel-session';
import PatientActivityContext from 'infrastructure/providers/patient-activity-context';
import BlockedSessionContext from 'infrastructure/providers/blocked-session-context';
import UnsavedChangesProvider, {
  useUnsavedChanges,
} from 'infrastructure/providers/unsaved-changes/unsaved-changes';
import ChartSessionTimer from 'components/atoms/chart-session-timer';
import BlockedSessionModal from 'components/atoms/blocked-session-modal';
import {
  useAsyncEffect,
  useBlockedCountdown,
  useConfirmExit,
  useShowConfirmBeforeCloseTab,
} from 'infrastructure/hooks/utils';
import useAlert from 'infrastructure/hooks/utils/use-alert';
import useUser from 'utils/useUser';
import { Routes } from 'router/routes';

interface IPatientSessionContext {
  sessionId: string | null;
  onSwitch: (() => void) | null;
  switchLabel: string | null;
}

const PatientSessionContext = createContext<IPatientSessionContext>({
  sessionId: null,
  onSwitch: null,
  switchLabel: null,
});

export const usePatientSession = () => useContext(PatientSessionContext);

interface PatientSessionProviderProps {
  patientGuid: string;
  children: React.ReactNode;
}

const PatientSessionInner: React.FC<PatientSessionProviderProps> = ({
  patientGuid,
  children,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { Alert, showAlert } = useAlert();
  const { hasUnsavedChanges, showUnsavedModal } = useUnsavedChanges();
  const { isPatient, hasClinicalRole, isPractice, hasAdminPanelAccess } =
    useUser();

  const isIdleTimeRedirect = useRef(false);
  const isSessionSwitchRef = useRef(false);

  // Reset the switch flag once the navigation actually commits to a new path.
  // Both useConfirmExit blockers must read the flag before it resets — doing
  // it here (post-navigation) prevents the second blocker from missing it and
  // showing a spurious confirm dialog that double-proceeds into a 404.
  useEffect(() => {
    isSessionSwitchRef.current = false;
  }, [location.pathname]);

  const canRunSession = !isPatient && !isPractice && hasClinicalRole;

  const canTrackAdminPanel = !isPatient && !isPractice && !hasClinicalRole;

  const activePage: 'chart' | 'admin' = location.pathname.startsWith(
    '/admin/patient/',
  )
    ? 'admin'
    : 'chart';

  const pagesAccessedRef = useRef<Set<'chart' | 'admin'>>(new Set());

  useEffect(() => {
    pagesAccessedRef.current.add(activePage);
  }, [activePage]);

  const getPagesAccessed = (): 'chart' | 'admin' | 'both' | null => {
    const s = pagesAccessedRef.current;
    if (s.size === 0) return null;
    if (s.has('chart') && s.has('admin')) return 'both';
    return s.has('chart') ? 'chart' : 'admin';
  };

  const {
    sessionId,
    serverElapsedTime,
    rpmTime,
    ccmTime,
    idleState,
    onActivity,
    blocked,
    activePatientGuid,
    endSession,
  } = usePatientChartSession({
    patientGuid,
    canRun: canRunSession,
    onTimeout: () => {
      if (hasClinicalRole) {
        isIdleTimeRedirect.current = true;
        navigate(Routes.overviewDashboard, { replace: true });
      }
    },
    getPagesAccessed,
  });

  const {
    sessionId: adminSessionId,
    blocked: adminBlocked,
    activePatientGuid: adminActivePatientGuid,
  } = usePatientAdminPanelSession({
    patientGuid,
    canRun: canTrackAdminPanel && activePage === 'admin',
  });

  // Reset idle counter on page switch (same effect as user interaction)
  useEffect(() => {
    onActivity();
  }, [activePage]);

  // Input/change activity tracking
  useEffect(() => {
    if (!canRunSession || blocked) return;
    const handler = () => onActivity();
    document.addEventListener('input', handler, true);
    document.addEventListener('change', handler, true);
    return () => {
      document.removeEventListener('input', handler, true);
      document.removeEventListener('change', handler, true);
    };
  }, [canRunSession, blocked]);

  const {
    countdown: blockedCountdown,
    showModal: showBlockedModal,
    dismissModal: dismissBlockedModal,
  } = useBlockedCountdown(blocked, navigate);

  const {
    countdown: adminBlockedCountdown,
    showModal: showAdminBlockedModal,
    dismissModal: dismissAdminBlockedModal,
  } = useBlockedCountdown(adminBlocked, navigate);

  // Idle warning alert
  useAsyncEffect(async () => {
    if (hasClinicalRole && !isPractice && idleState === 'warning') {
      const result = await showAlert({
        title: '',
        messageTitle: t('messages.patientChartIdleWarning'),
        showReject: false,
        closable: false,
        successBtnLabel: t('labels.confirm'),
      });
      if (onActivity && result) {
        onActivity();
      }
    }
  }, [hasClinicalRole, idleState, isPractice]);

  const shouldBlockNav =
    (!blocked && (canRunSession || hasUnsavedChanges)) ||
    (!adminBlocked && canTrackAdminPanel && activePage === 'admin');

  // Confirm before leaving patient context — single blocker (React Router only
  // honours the last-registered blocker when multiple are active).
  useConfirmExit(async () => {
    if (isIdleTimeRedirect.current) return true;
    if (isSessionSwitchRef.current) return true;

    if (canRunSession || hasUnsavedChanges) {
      let res: boolean = true;
      if (hasUnsavedChanges) {
        res = await showUnsavedModal();
        if (res) endSession();
        return res;
      }
      res = await showAlert({
        title: '',
        successBtnLabel: t('labels.confirm'),
        messageTitle: t('messages.closeChartSession'),
        closable: false,
      });
      if (res) endSession();
      return res;
    }

    // canTrackAdminPanel path
    return showAlert({
      title: '',
      successBtnLabel: t('labels.confirm'),
      messageTitle: t('messages.closeAdminPanelSession'),
      closable: false,
    });
  }, shouldBlockNav);

  useShowConfirmBeforeCloseTab(!blocked && canRunSession);

  const handleCloseSession = () => {
    navigate(Routes.overviewDashboard);
  };

  const handleSwitch = () => {
    isSessionSwitchRef.current = true;
    if (activePage === 'chart') {
      navigate(Routes.adminPatient(patientGuid));
    } else {
      navigate(Routes.patientGuid(patientGuid));
    }
    onActivity();
  };

  const closeLabel = t('labels.closeChart');
  const switchLabel =
    activePage === 'chart' ? t('labels.goToAdminPanel') : t('labels.goToChart');

  return (
    <PatientSessionContext.Provider
      value={{
        sessionId,
        onSwitch: canRunSession && hasAdminPanelAccess ? handleSwitch : null,
        switchLabel,
      }}
    >
      <PatientActivityContext.Provider value={{ onActivity }}>
        <BlockedSessionContext.Provider value={blocked}>
          {children}
        </BlockedSessionContext.Provider>
      </PatientActivityContext.Provider>
      {canRunSession && patientGuid && (
        <ChartSessionTimer
          sessionId={sessionId}
          serverElapsedTime={serverElapsedTime}
          rpmTime={rpmTime}
          ccmTime={ccmTime}
          onClose={handleCloseSession}
          closeLabel={closeLabel}
        />
      )}
      {canTrackAdminPanel && patientGuid && (
        <ChartSessionTimer
          sessionId={adminSessionId}
          serverElapsedTime={0}
          rpmTime={0}
          ccmTime={0}
          onClose={handleCloseSession}
          closeLabel={closeLabel}
          hideTimer
        />
      )}
      {blocked && activePatientGuid && showBlockedModal && (
        <BlockedSessionModal
          activePatientGuid={activePatientGuid}
          countdown={blockedCountdown}
          onDismiss={dismissBlockedModal}
        />
      )}
      {adminBlocked && adminActivePatientGuid && showAdminBlockedModal && (
        <BlockedSessionModal
          activePatientGuid={adminActivePatientGuid}
          countdown={adminBlockedCountdown}
          onDismiss={dismissAdminBlockedModal}
        />
      )}
      {Alert}
    </PatientSessionContext.Provider>
  );
};

const PatientSessionProvider: React.FC<PatientSessionProviderProps> = ({
  patientGuid,
  children,
}) => {
  return (
    <UnsavedChangesProvider>
      <PatientSessionInner patientGuid={patientGuid}>
        {children}
      </PatientSessionInner>
    </UnsavedChangesProvider>
  );
};

export default PatientSessionProvider;
